// frontend/src/pages/Admin/EmailGroups.js
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Input, Popconfirm, Badge, Select, Modal, Tag } from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
  TeamOutlined, CheckCircleOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import api from '../../utils/api';
import { useNotificationApi } from '../../context/NotificationContext';
import EmailGroupForm from './EmailGroupForm';
import EmailGroupMemberForm from './EmailGroupMemberForm';
import dayjs from 'dayjs';

const { Search } = Input;
const { Option } = Select;

const EmailGroups = () => {
  const [emailGroups, setEmailGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isGroupModalVisible, setIsGroupModalVisible] = useState(false);
  const [isMemberModalVisible, setIsMemberModalVisible] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [managingMembersGroup, setManagingMembersGroup] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [deactivatingId, setDeactivatingId] = useState(null);
  const [activatingId, setActivatingId] = useState(null);
  const [hardDeletingId, setHardDeletingId] = useState(null);

  const notificationApi = useNotificationApi();

  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300;
    const minDisplayTime = 500;

    const startTime = Date.now();

    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        if (dismissNotification) {
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  const fetchEmailGroups = useCallback(async (triggeredByAction = false) => {
    setLoading(true);
    const loadingKey = 'fetchEmailGroupsLoading';
    const hideLoading = showLoading(loadingKey, 'Loading email groups...');

    try {
      const params = {
        status: filterStatus,
      };

      if (searchText) {
        params.search = searchText;
      }

      const response = await api.get('/api/email-groups', { params });
      let fetchedGroups = response.data.data || [];

      if (!Array.isArray(fetchedGroups)) {
        console.warn('EmailGroups.js: API response for email groups was not an array. Assuming empty array.');
        fetchedGroups = [];
      }

      setEmailGroups(fetchedGroups);
      hideLoading();

      if (!triggeredByAction) {
        showSuccess('Email Groups Loaded', 'Email group list fetched successfully!', 'fetchEmailGroupsSuccess');
      }
    } catch (error) {
      hideLoading();
      console.error('Error fetching email groups:', error.response?.data || error.message, error);
      showError('Failed to Fetch Email Groups', error.response?.data?.message || 'An unexpected error occurred while fetching email groups.', 'fetchEmailGroupsError');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, searchText, showLoading, showSuccess, showError]);

  useEffect(() => {
    fetchEmailGroups();
  }, [fetchEmailGroups]);

  const handleAddGroup = () => {
    setEditingGroup(null);
    setIsGroupModalVisible(true);
  };

  const handleEditGroup = (record) => {
    setEditingGroup(record);
    setIsGroupModalVisible(true);
  };

  const handleDeactivateGroup = async (groupId, groupName) => {
    setDeactivatingId(groupId);
    const loadingKey = `emailGroupStatusUpdate-${groupId}`;
    const hideLoading = showLoading(loadingKey, `Deactivating ${groupName}...`);

    try {
      await api.patch(`/api/email-groups/${groupId}/deactivate`);
      hideLoading();
      showSuccess('Group Status Updated', `${groupName} was deactivated.`, 'emailGroupStatusUpdateSuccess');
      
      fetchEmailGroups(true);      
    } catch (error) {
      hideLoading();
      console.error('Error deactivating email group:', error.response?.data || error.message, error);
      showError('Cannot Deactivate Group', error.response?.data?.message || `Failed to deactivate ${groupName}.`, `deactivateEmailGroupError-${groupId}`);
    } finally {
      setDeactivatingId(null);
    }
  };

  const handleActivateGroup = async (groupId, groupName) => {
    setActivatingId(groupId);
    const loadingKey = `emailGroupStatusUpdate-${groupId}`;
    const hideLoading = showLoading(loadingKey, `Activating ${groupName}...`);

    try {
      await api.patch(`/api/email-groups/${groupId}/activate`);
      hideLoading();
      showSuccess('Group Status Updated', `${groupName} was activated.`, 'emailGroupStatusUpdateSuccess');
      
      if (filterStatus === 'inactive') {
        setFilterStatus('active');
      }
      fetchEmailGroups(true);
    } catch (error) {
      hideLoading();
      console.error('Error activating email group:', error.response?.data || error.message, error);
      showError('Cannot Activate Group', error.response?.data?.message || `Failed to activate ${groupName}.`, `activateEmailGroupError-${groupId}`);
    } finally {
      setActivatingId(null);
    }
  };

  const handleHardDeleteGroup = async (groupId, groupName) => {
    setHardDeletingId(groupId);
    const loadingKey = `emailGroupHardDelete-${groupId}`;
    const hideLoading = showLoading(loadingKey, `Permanently deleting ${groupName}...`);

    try {
      await api.delete(`/api/email-groups/${groupId}`);
      hideLoading();
      showSuccess('Group Deleted', `Email group "${groupName}" permanently deleted successfully!`, 'emailGroupHardDeleteSuccess');
      fetchEmailGroups(true);
    } catch (error) {
      hideLoading();
      console.error('Error hard deleting email group:', error.response?.data || error.message, error);
      showError('Cannot Delete Group', error.response?.data?.message || `Failed to hard delete email group "${groupName}".`, `hardDeleteEmailGroupError-${groupId}`);
    } finally {
      setHardDeletingId(null);
    }
  };

  const handleManageMembers = (record) => {
    setManagingMembersGroup(record);
    setIsMemberModalVisible(true);
  };

  const filteredGroups = (emailGroups || []).filter(group => {
    const matchesSearch = (group.name || '').toLowerCase().includes(searchText.toLowerCase()) || 
                         (group.groupEmailAddress || '').toLowerCase().includes(searchText.toLowerCase()) ||
                         (group.description || '').toLowerCase().includes(searchText.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && group.isActive && !group.deletedAt) ||
                         (filterStatus === 'inactive' && !group.isActive && !group.deletedAt) ||
                         (filterStatus === 'all' && group.deletedAt);
    
    return matchesSearch && matchesStatus;
  });

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a, b) => a.id - b.id,
      defaultSortOrder: 'descend',
      width: '8%',
    },
    {
      title: 'Group Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      width: '20%',
    },
    {
      title: 'Email Address',
      dataIndex: 'groupEmailAddress',
      key: 'groupEmailAddress',
      sorter: (a, b) => (a.groupEmailAddress || '').localeCompare(b.groupEmailAddress || ''),
      width: '20%',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      width: '20%',
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive, record) => (
        <>
          {record.deletedAt ? (
            <Badge status="default" text="Deleted" />
          ) : (
            <Tag 
              icon={isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
              color={isActive ? 'success' : 'error'}
            >
              {isActive ? 'Active' : 'Inactive'}
            </Tag>
          )}
        </>
      ),
      width: '10%',
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Inactive', value: 'inactive' },
        { text: 'All', value: 'all' },
      ],
      onFilter: (value, record) => {
        if (value === 'active') return record.isActive && !record.deletedAt;
        if (value === 'inactive') return !record.isActive && !record.deletedAt;
        return true; // 'all' filter
      },
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => text ? dayjs(text).format('DD/MM/YYYY HH:mm') : 'N/A',
      sorter: (a, b) => {
        const dateA = a.createdAt ? dayjs(a.createdAt).valueOf() : 0;
        const dateB = b.createdAt ? dayjs(b.createdAt).valueOf() : 0;
        return dateA - dateB;
      },
      width: '15%',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '20%',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<TeamOutlined />}
            onClick={() => handleManageMembers(record)}
            aria-label={`Manage Members for ${record.name}`}
            disabled={record.deletedAt}
            title="Manage Members"
          />

          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditGroup(record)}
            aria-label={`Edit ${record.name}`}
            disabled={record.deletedAt}
            title="Edit Group"
          />

          {record.deletedAt ? null : (
            <>
              {record.isActive ? (
                <Popconfirm
                  title={`Are you sure you want to deactivate "${record.name}"?`}
                  onConfirm={() => handleDeactivateGroup(record.id, record.name)}
                  okText="Yes"
                  cancelText="No"
                  okButtonProps={{ loading: deactivatingId === record.id, danger: true }}
                >
                  <Button 
                    type="link" 
                    danger 
                    icon={<CloseCircleOutlined />} 
                    aria-label={`Deactivate ${record.name}`}
                    title="Deactivate Group"
                  />
                </Popconfirm>
              ) : (
                <Popconfirm
                  title={`Are you sure you want to activate "${record.name}"?`}
                  onConfirm={() => handleActivateGroup(record.id, record.name)}
                  okText="Yes"
                  cancelText="No"
                  okButtonProps={{ loading: activatingId === record.id }}
                >
                  <Button 
                    type="link" 
                    icon={<CheckCircleOutlined />} 
                    aria-label={`Activate ${record.name}`}
                    title="Activate Group"
                  />
                </Popconfirm>
              )}
            </>
          )}

          <Popconfirm
            title={`Are you absolutely sure you want to PERMANENTLY DELETE the group "${record.name}"? This cannot be undone and will fail if members are linked.`}
            onConfirm={() => handleHardDeleteGroup(record.id, record.name)}
            okText="Yes, Delete Permanently"
            cancelText="No, Keep It"
            okButtonProps={{ danger: true, loading: hardDeletingId === record.id }}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              aria-label={`Hard Delete ${record.name}`}
              title="Delete Group Permanently"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="email-groups-page">
      <div className="email-groups-header">
        <h2>Email Group Management</h2>
        <div className="email-groups-actions">
          <Space size="middle">
            <Search
              placeholder="Search groups..."
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={() => fetchEmailGroups()}
              style={{ width: 300 }}
            />
            <Select
              defaultValue="active"
              size="large"
              style={{ width: 150 }}
              value={filterStatus}
              onChange={(value) => {
                setFilterStatus(value);
                fetchEmailGroups();
              }}
            >
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
              <Option value="all">All Status</Option>
            </Select>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddGroup}
              size="large"
            >
              Add Group
            </Button>
          </Space>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={filteredGroups}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: true }}
      />

      <EmailGroupForm
        visible={isGroupModalVisible}
        onCancel={() => setIsGroupModalVisible(false)}
        onSuccess={() => fetchEmailGroups(true)}
        emailGroup={editingGroup}
      />

      <EmailGroupMemberForm
        visible={isMemberModalVisible}
        emailGroup={managingMembersGroup}
        onCancel={() => setIsMemberModalVisible(false)}
        onSuccess={() => {
          setIsMemberModalVisible(false);
          // No need to re-fetch groups, only members updated
        }}
      />
    </div>
  );
};

export default EmailGroups;